import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, register as apiRegister, sendOTP } from '../services/api';
import { toast } from 'react-toastify';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const loginStatus = localStorage.getItem('LoginUser');
      const userId = localStorage.getItem('UserId');
      const username = localStorage.getItem('Username');

      if (loginStatus && userId) {
        setUser({ id: userId, name: username });
        setIsAuthenticated(true);
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (credentials) => {
    try {
      const response = await apiLogin(credentials);
      const { data, success, message } = response.data;

      if (success) {
        localStorage.setItem('LoginUser', true);
        localStorage.setItem('UserId', data._id);
        localStorage.setItem('Username', data.FirstName);
        setUser({ id: data._id, name: data.FirstName, ...data });
        setIsAuthenticated(true);
        setShowLoginModal(false);
        toast.success(message);
        return { success: true };
      } else {
        toast.error(message);
        return { success: false, message };
      }
    } catch (error) {
      toast.error('Login failed. Please try again.');
      return { success: false, message: 'Login failed' };
    }
  };

  const register = async (userData, otp, randomOTP) => {
    if (randomOTP !== Number(otp)) {
      toast.error('OTP Incorrect');
      return { success: false, message: 'OTP Incorrect' };
    }

    try {
      const response = await apiRegister(userData);
      const { data, message } = response.data;

      localStorage.setItem('LoginUser', true);
      localStorage.setItem('UserId', data._id);
      localStorage.setItem('Username', data.FirstName);
      setUser({ id: data._id, name: data.FirstName, ...data });
      setIsAuthenticated(true);
      setShowRegisterModal(false);
      toast.success(message);
      return { success: true };
    } catch (error) {
      // Handle specific error messages from backend
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
        return { success: false, message: error.response.data.message };
      }
      toast.error('Registration failed. Please try again.');
      return { success: false, message: 'Registration failed' };
    }
  };

  const requestOTP = async (email) => {
    const otp = Math.floor(Math.random() * (999999 - 100000 + 1)) + 100000;
    try {
      await sendOTP({ email: email, otp });
      toast.success('OTP Sent Successfully');
      return { success: true, otp };
    } catch (error) {
      toast.error('Failed to send OTP');
      return { success: false };
    }
  };

  const logout = () => {
    localStorage.removeItem('LoginUser');
    localStorage.removeItem('UserId');
    localStorage.removeItem('Username');
    localStorage.removeItem('cartdata');
    localStorage.removeItem('wishlist');
    setUser(null);
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const openLoginModal = () => {
    setShowRegisterModal(false);
    setShowLoginModal(true);
  };

  const openRegisterModal = () => {
    setShowLoginModal(false);
    setShowRegisterModal(true);
  };

  const closeModals = () => {
    setShowLoginModal(false);
    setShowRegisterModal(false);
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    requestOTP,
    showLoginModal,
    showRegisterModal,
    openLoginModal,
    openRegisterModal,
    closeModals,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
